import { Button, Checkbox, Form, Input, Upload } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { uploadButton } from "components/UploadButton/UploadButton";
import { getBase64 } from "helpers/functions";
import { Article } from "interfaces/websiteContent";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";

interface Props {
  newArticle: boolean;
  article?: Article;
  handleCreateOrEdit: (article: Article) => void;
}

const CreateOrEditArticle: React.FC<Props> = ({ newArticle, article, handleCreateOrEdit }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const publishArticle = (values: Article) => {
    if (article) return;
    setLoading(true);
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
    formData.append("updated_at", new Date().toISOString());
    formData.append("created_at", new Date().toISOString());
    if (file) {
      formData.append("big_image", file);
    }

    axios
      .post<Article>("/website-contents/articles/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        message("success", "Artikkel publisert");
        handleCreateOrEdit(response.data);
      })
      .catch(() => message("error", "Kunne ikke publisere artikkel"))
      .finally(() => setLoading(false));
  };

  const updateArticle = (values: Article) => {
    if (!article) return;
    setLoading(true);

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
    formData.append("updated_at", new Date().toISOString());
    if (file) {
      formData.append("big_image", file);
    }
    if (removeImage) {
      formData.append("big_image", "");
    }
    axios
      .patch<Article>(`/website-contents/articles/${article.id}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(response => {
        message("success", "Artikkel oppdatert");
        handleCreateOrEdit(response.data);
      })
      .catch(() => message("error", "Kunne ikke oppdatere artikkel"))
      .finally(() => setLoading(false));
  };

  useEffect(
    function setPreviewImage() {
      if (file) {
        getBase64(file)
          .then((result: string | null) => {
            setPreview(result);
          })
          .catch(() => {
            setPreview(null);
          });
      }
    },
    [file]
  );

  useEffect(() => {
    if (article) {
      setFile(null);
      setPreview(null);
      setRemoveImage(false);
    }
  }, [article]);

  return (
    <Form form={form} layout="vertical" requiredMark={false} onFinish={newArticle ? publishArticle : updateArticle}>
      <Form.Item
        label="Tittel"
        name="title"
        initialValue={article?.title}
        rules={[{ required: true, message: "Legg til tittel" }]}
      >
        <Input placeholder="Skriv inn tittel..." style={{ marginBottom: "20px" }} />
      </Form.Item>
      <Form.Item
        label="Innhold"
        name="content"
        initialValue={article?.content}
        rules={[{ required: true, message: "Legg til innhold" }]}
      >
        <ReactQuill theme="snow" style={{ marginBottom: "20px" }} />
      </Form.Item>
      <Form.Item label="Bilde" rules={[{ required: true, message: "Legg til et bilde" }]}>
        <Upload
          listType="picture-card"
          className="avatar-uploader"
          maxCount={2}
          beforeUpload={f => {
            setFile(f);
            setRemoveImage(false);
            return false;
          }}
          onRemove={() => {
            setFile(null);
            setPreview(null);
            setRemoveImage(true);
          }}
          fileList={
            removeImage
              ? []
              : file
              ? preview
                ? [
                    {
                      uid: "-1",
                      name: "New article image",
                      url: preview,
                    },
                  ]
                : []
              : article?.big_image
              ? [
                  {
                    uid: "-1",
                    name: article?.big_image?.substring(article?.big_image?.lastIndexOf("/") + 1) || "Bilde",
                    status: "done",
                    url: article?.big_image,
                  },
                ]
              : []
          }
        >
          {uploadButton}
        </Upload>
      </Form.Item>
      <Form.Item
        label="Vis på forsiden"
        name="show_on_frontpage"
        initialValue={newArticle ? true : article?.show_on_frontpage}
        valuePropName="checked"
      >
        <Checkbox style={{ width: "100%", marginBottom: "20px" }} />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={loading} style={{ width: "100%" }}>
        {newArticle ? "Publiser artikkel" : "Oppdater artikkel"}
      </Button>
    </Form>
  );
};

export default CreateOrEditArticle;
